import { ROUTES } from '@/shared/lib/constants';
import { parseFqnToPathParts } from '@/shared/lib/format';

/**
 * 태그 FQN을 기반으로 적절한 라우트를 반환하는 유틸 함수
 *
 * @description
 * - 점(.)이 없는 최상위 카테고리 → /tags/{category} (카테고리 목록 페이지)
 * - 점(.)이 있는 하위 태그 → /tags/detail/{fullFQN} (태그 상세 페이지)
 *
 * @example
 * getTagRoute('PersonalData') → '/tags/PersonalData'
 * getTagRoute('PersonalData.Personal') → '/tags/detail/PersonalData.Personal'
 * getTagRoute('cyclone.데이터 자산') → '/tags/detail/cyclone.데이터 자산'
 */
export const getTagRoute = (tagFQN: string): string => {
  if (!tagFQN) return '/tags';

  if (tagFQN.includes('.')) {
    return `${ROUTES.TAGS_DETAIL}/${encodeURIComponent(tagFQN)}`;
  }

  return `${ROUTES.TAGS}/${encodeURIComponent(tagFQN)}`;
};

/**
 * 태그 FQN을 분리하여 카테고리와 태그명을 반환
 *
 * @description
 * parseFqnToPathParts를 사용하여 따옴표로 감싸진 부분을 올바르게 파싱합니다.
 *
 * @example
 * parseTagFQN('PersonalData.Personal') → { category: 'PersonalData', tagName: 'Personal' }
 * parseTagFQN('cyclone.데이터 자산.데이터베이스') → { category: 'cyclone', tagName: '데이터 자산.데이터베이스' }
 * parseTagFQN('"category.name".tag') → { category: 'category.name', tagName: 'tag' }
 */
export const parseTagFQN = (tagFQN: string): { category: string; tagName: string | null } => {
  const parts = parseFqnToPathParts(tagFQN);

  if (parts.length === 0) {
    return { category: '', tagName: null };
  }

  if (parts.length === 1) {
    return { category: parts[0], tagName: null };
  }

  return {
    category: parts[0],
    tagName: parts.slice(1).join('.'),
  };
};
